import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';
import useCookieConsent, { OPEN_EVENT } from 'hooks/useCookieConsent';
import { findKeyByRewriteUrl } from 'urlTranslations';

// Asks before any analytics or advertising cookie is set (WHIT-421). Accept
// and Reject carry equal weight; "Cookie settings" in the footer reopens it.
const CookieConsent: React.FC = () => {
  const { consent, loaded, choose } = useCookieConsent();
  const [reopened, setReopened] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    const open = () => setReopened(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  if (!loaded || (consent !== null && !reopened)) return null;

  const decide = async (choice: 'accepted' | 'rejected') => {
    if (await choose(choice)) setReopened(false);
  };
  const buttonClass =
    'min-w-[8rem] rounded-lg border-2 border-tt-dark-purple px-4 py-2 font-bold text-tt-dark-purple bg-white hover:bg-tt-white-purple';

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('Cookie settings') || ''}
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white px-4 py-5 shadow-lg">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 md:flex-row md:items-center">
        <p className="flex-1 text-sm text-tt-verydark-purple">
          {t('cookie-consent')}{' '}
          <Link
            href={findKeyByRewriteUrl('/privacy-policy/', router.locale)}
            className="underline">
            {t('Privacy policy')}
          </Link>
        </p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            className={buttonClass}
            onClick={() => decide('rejected')}>
            {t('Reject')}
          </button>
          <button
            type="button"
            className={buttonClass}
            onClick={() => decide('accepted')}>
            {t('Accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
